<template>
  <RevDrawer
    back-button-label="back"
    close-button-label="close"
    data-test="modal-trade-in-au"
    :name="props.modalName"
    :title="modalTitle"
    @close="handleClose"
  >
    <template #body>
      <div v-if="isLoading" class="mt-6 flex items-center justify-center">
        <RevLoadingScreen :text="spinnerLoadText" />
      </div>
      <div v-else>
        <div v-if="offer === undefined && question">
          <h3 class="heading-3 mb-12">
            {{ question.title }}
          </h3>
          <RevInputSelect
            v-if="question.type === 'select'"
            :id="question.key"
            v-model="selectedValue"
            class="mt-8"
            :data-qa="question.key"
            label=""
            :options="question.options"
            @update:model-value="onSelect"
          />
          <div
            v-if="question.type === 'radioline'"
            class="grid grid-cols-1 gap-x-8 gap-y-12"
            :data-qa="question.key"
          >
            <div
              v-for="option in question.options"
              :key="option.value"
              class="w-full"
            >
              <RevPicker
                :id="option.value"
                :alignment="option.description ? 'left' : 'center'"
                class="h-full w-full"
                :data-qa="`${question.key}-${option.value}`"
                :description="option.description"
                :label="option.label"
                :model-value="selectedValue === option.value"
                @update:model-value="
                  handleRefreshRenewableQuestion(
                    question.key,
                    option.value,
                    question.lastQuestion,
                  )
                "
              />
            </div>
          </div>
          <div
            v-if="question.infoBlock"
            class="text-action-default-low body-2 my-8 flex flex-col gap-8"
          >
            <p v-for="text in question.infoBlock.paragraphs" :key="text">
              {{ text }}
            </p>
          </div>
        </div>
        <div v-else-if="offer" class="flex flex-col gap-y-8">
          <TradeInOffer
            class="mb-8"
            :name="offer.productShortTitle"
            :price="offer.price"
          />
          <RevForm
            :initial-values="initialAcceptOfferValues"
            :validate
            @submit="acceptOffer"
          >
            <template #default="{ values, errors }">
              <RevInputText
                v-if="!isUserAuthenticated"
                id="anonymousCustomerEmail"
                v-model="
                  (values as typeof initialAcceptOfferValues)
                    .anonymousCustomerEmail
                "
                :error="
                  (errors as Record<string, string>).anonymousCustomerEmail
                "
                :icon="IconMail"
                :label="i18n(translations.inputEmailLabel)"
              />
              <RevButton
                class="mt-[20px]"
                data-qa="accept-offer"
                data-test="accept-offer"
                :disabled="
                  acceptOfferDisabled(
                    values as FormValues,
                    errors as Record<string, string>,
                  )
                "
                full-width="always"
                type="submit"
                variant="primary"
              >
                {{ i18n(translations.acceptOffer) }}
              </RevButton>
            </template>
          </RevForm>
        </div>
        <div v-else>
          <div class="flex flex-col gap-y-12">
            <div class="flex flex-col items-center text-center">
              <IconSwap class="mb-16 h-48 w-48" />
              <div class="w-full">
                <h2 class="heading-2">
                  {{ i18n(translations.summaryTitle) }}
                </h2>
                <p class="body-2">
                  {{ i18n(translations.summaryText) }}
                </p>
              </div>
            </div>
            <div class="flex flex-col gap-20 pt-20">
              <RevCard
                v-for="step in summarySteps"
                :key="step.title.id"
                class="py-15 px-20"
              >
                <div class="flex flex-col gap-4">
                  <p>
                    <span class="mr-12">{{ i18n(step.title) }}</span>
                    <span class="heading-2">{{ i18n(step.subTitle) }}</span>
                  </p>
                  <p class="text-static-default-low body-2">
                    {{ i18n(step.text) }}
                  </p>
                </div>
              </RevCard>
            </div>
            <div class="flex flex-col gap-10 pt-20">
              <RevButton
                data-qa="start-trade-in"
                data-test="start-trade-in"
                full-width="always"
                variant="primary"
                @click="getQuestion"
              >
                {{ i18n(translations.startTradeIn) }}
              </RevButton>
            </div>
          </div>
        </div>
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'

import type { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevDrawer } from '@ds/components/Drawer'
import {
  RevForm,
  makeValidate,
  matchingRegExp,
  required,
} from '@ds/components/Form'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { closeModal } from '@ds/components/ModalBase'
import { RevPicker } from '@ds/components/Picker'
import { IconMail } from '@ds/icons/IconMail'
import { IconSwap } from '@ds/icons/IconSwap'

import type { Answer, Offer, Question } from '~/api/tradein-au/types'
import { REGEXP_VALID_EMAIL } from '~/constants/regex'
import TradeInOffer from '~/scopes/tradein-au/components/TradeInOffer/TradeInOffer.vue'
import { useTradeInAuOffer } from '~/scopes/tradein-au/composables/useTradeInAuOffer'

import translations from './TradeInModal.translations'

export interface ModalProps {
  modalName: string
}

const i18n = useI18n()
const logger = useLogger()
const $httpFetch = useHttpFetch()
const { openSuccessToast, openErrorToast } = useTheToast()
const { isUserAuthenticated } = useUserStore()
const pathParamMarketValue = useMarketplace().market.countryCode.toLowerCase()

const props = defineProps<ModalProps>()
const { setAcceptedOffer } = useTradeInAuOffer()
const question = ref<Question>()
const offer = ref<Offer>()

const modalTitle = i18n(translations.modalTitle)
const spinnerLoadText = i18n(translations.spinnerLoadText)

const answers = ref<Answer[]>([])
const isLoading = ref(false)

type FormValues = {
  anonymousCustomerEmail: string
}

const initialAcceptOfferValues = reactive<FormValues>({
  anonymousCustomerEmail: '',
})

const validate = computed(() =>
  isUserAuthenticated.value
    ? makeValidate<FormValues>({})
    : makeValidate<FormValues>({
        anonymousCustomerEmail: [
          required(i18n(translations.emailRequiredError)),
          matchingRegExp(
            REGEXP_VALID_EMAIL,
            i18n(translations.emailInvalidError),
          ),
        ],
      }),
)

const summarySteps = [
  {
    title: translations.step1Title,
    subTitle: translations.step1SubTitle,
    text: translations.step1Text,
  },
  {
    title: translations.step2Title,
    subTitle: translations.step2SubTitle,
    text: translations.step2Text,
  },
  {
    title: translations.step3Title,
    subTitle: translations.step3SubTitle,
    text: translations.step3Text,
  },
]

function resetFunnel(): void {
  question.value = undefined
  offer.value = undefined
  answers.value = []
}

function handleClose() {
  resetFunnel()
}

const selectedValue = ref<string | null>(null)

async function getQuestion(): Promise<void> {
  try {
    isLoading.value = true
    question.value = await $httpFetch(
      '/api/apac-trade-in/:market/v1/next-question',
      {
        method: 'POST',
        pathParams: {
          market: pathParamMarketValue,
        },
        body: {
          answers: answers.value,
        },
      },
    )
  } catch (err) {
    openErrorToast()
    logger.error('[TRADE-IN-AU][API] Error fetching funnel questions', {
      error: err as HttpApiError,
      owners: ['bot-squad-apac-front'],
    })
  } finally {
    isLoading.value = false
  }
}

async function getOffer(): Promise<void> {
  try {
    isLoading.value = true
    offer.value = await $httpFetch('/api/apac-trade-in/:market/v1/offer', {
      method: 'POST',
      pathParams: {
        market: pathParamMarketValue,
      },
      body: {
        answers: answers.value,
      },
    })
  } catch (err) {
    openErrorToast()
    logger.error('[TRADE-IN-AU][API] Error fetching offer', {
      error: err as HttpApiError,
      owners: ['bot-squad-apac-front'],
    })
  } finally {
    isLoading.value = false
  }
}

async function acceptOffer(values: FormValues): Promise<void> {
  if (!offer.value) return

  try {
    isLoading.value = true

    await $httpFetch('/api/apac-trade-in/:market/v1/accept-offer', {
      method: 'POST',
      pathParams: {
        market: pathParamMarketValue,
      },
      body: {
        offer: offer.value,
        ...(isUserAuthenticated.value
          ? {}
          : { email: values.anonymousCustomerEmail }),
      },
    })

    setAcceptedOffer(offer.value)
    openSuccessToast({
      title: i18n(translations.successAcceptOfferTitle),
      content: i18n(translations.successAcceptOffer),
    })

    resetFunnel()
    closeModal(props.modalName)
  } catch (err) {
    openErrorToast()
    logger.error('[TRADE-IN-AU][API] Error accepting offer', {
      error: err as HttpApiError,
      owners: ['bot-squad-apac-front'],
    })
  } finally {
    isLoading.value = false
  }
}

function onSelect(event: unknown) {
  if (!question.value) return

  let extracted: string | null = null

  if (typeof event === 'string') {
    extracted = event
  } else if (typeof event === 'object' && event !== null && 'value' in event) {
    const maybeValue = event.value
    if (typeof maybeValue === 'string') {
      extracted = maybeValue
    }
  }

  if (!extracted) return

  handleRefreshRenewableQuestion(
    question.value.key,
    extracted,
    question.value.lastQuestion,
  )
}

async function handleRefreshRenewableQuestion(
  key: string,
  value: string | null,
  lastQuestion: boolean,
) {
  if (!value) return

  answers.value.push({ key, value })

  if (lastQuestion === false) {
    await getQuestion()
  } else {
    await getOffer()
  }
}

function acceptOfferDisabled(
  values: FormValues,
  errors: Record<string, string>,
): boolean {
  if (isUserAuthenticated.value) return false

  return !values.anonymousCustomerEmail || !!errors.anonymousCustomerEmail
}
</script>
